import { useState } from 'react'
import {
  ShoppingCart,
  Trash2,
  Send,
  AlertTriangle,
  X,
  Loader2
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { useCart, type CartItem } from '@store/cart/CartProvider'

export function CartModal() {
  const { items, count, isOpen, setOpen, updateQty, removeItem, clearCart, confirmOrder, isLoading } = useCart()
  const [confirmResult, setConfirmResult] = useState<{ success: boolean; message: string; itemCount: number } | null>(null)

  const handleConfirm = async () => {
    const result = await confirmOrder('ZWEB')
    setConfirmResult(result)
  }

  const handleClose = () => {
    setOpen(false)
    setConfirmResult(null)
  }

  const handleClear = async () => {
    await clearCart()
  }

  const totalUnidades = items.reduce((sum, item) => sum + item.unidades, 0)

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-orange-500" />
            Cesta de Transfer
            {count > 0 && (
              <span className="text-sm font-normal text-gray-500">({count} articulo{count !== 1 ? 's' : ''})</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {confirmResult ? (
          <div className="py-8 text-center space-y-4">
            {confirmResult.success ? (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <ShoppingCart className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-700">Pedido enviado correctamente</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {confirmResult.itemCount} articulo{confirmResult.itemCount !== 1 ? 's' : ''} enviado{confirmResult.itemCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-700">Error al enviar</h3>
                  <p className="text-sm text-gray-500 mt-1">{confirmResult.message}</p>
                </div>
              </>
            )}
            <Button variant="outline" onClick={handleClose} className="mt-4">
              Cerrar
            </Button>
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <div className="py-12 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">La cesta esta vacia</p>
                <p className="text-sm text-gray-400 mt-1">
                  Pulse el icono de carrito en las lineas de transfer para anadir articulos
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2 font-medium text-gray-600 w-8"></th>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Codigo</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Nombre</th>
                        <th className="text-center px-3 py-2 font-medium text-gray-600 w-24">Uds.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item: CartItem) => (
                        <tr key={item.id} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              title="Quitar"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-gray-600">
                            {String(item.matnr).replace(/^0+/, '')}
                          </td>
                          <td className="px-3 py-2 text-gray-700 truncate max-w-[200px]" title={item.nombre}>
                            {item.nombre}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.unidades}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10)
                                if (v > 0) updateQty(item.id, v)
                              }}
                              className="w-20 h-8 text-center text-sm"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-gray-500">
                    Total: <span className="font-semibold">{totalUnidades}</span> ud{totalUnidades !== 1 ? 's.' : '.'}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Vaciar cesta
                  </Button>
                </div>
              </div>
            )}

            {items.length > 0 && (
              <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                <Button variant="outline" onClick={handleClose}>
                  Cerrar
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Confirmar Transfer
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
